import * as React from "react";

import { cn } from "@/lib/utils";

// Above this many concurrent placeholders, CSS swaps the sweep for a cheaper pulse
const MANY_SHIMMERS = 24;
let activeShimmers = 0;

function trackShimmer(delta: number) {
  activeShimmers += delta;
  document.documentElement.toggleAttribute("data-many-shimmers", activeShimmers > MANY_SHIMMERS);
}

interface LoadingImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Classes for the wrapper that holds the shimmer placeholder. */
  wrapperClassName?: string;
}

/**
 * Image that shows a shimmer placeholder until it loads, then fades and
 * sharpens in. Already-cached images skip the placeholder.
 */
export function LoadingImage({ wrapperClassName, className, src, onLoad, ...props }: LoadingImageProps) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [loadedSrc, setLoadedSrc] = React.useState<string | null>(null);
  const isLoaded = !!src && loadedSrc === src;

  // A cached image can finish before React attaches onLoad
  React.useLayoutEffect(() => {
    const img = imgRef.current;
    if (img && src && img.complete && img.naturalWidth > 0) setLoadedSrc(src);
  }, [src]);

  React.useEffect(() => {
    if (isLoaded) return;
    trackShimmer(1);
    return () => trackShimmer(-1);
  }, [isLoaded]);

  return (
    <div className={cn("relative", !isLoaded && "loading-shimmer", wrapperClassName)}>
      <img
        ref={imgRef}
        src={src}
        onLoad={(e) => {
          setLoadedSrc(src ?? null);
          onLoad?.(e);
        }}
        className={cn(
          "transition-[opacity,filter] duration-300 ease-out",
          isLoaded ? "opacity-100 blur-0" : "opacity-0 motion-safe:blur-sm",
          className,
        )}
        {...props}
      />
    </div>
  );
}
